import { ProductivityRequester, type ProductivitySummaryRequest, type ProductivityTimeSeriesChartRequest, type TimeseriesChart } from '@cobliteam/api-lib';

import type { ProductivitySummary } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getProductivityRequester = () => new ProductivityRequester({ host: env.apiHost });
const DRIVER_QUERY_KEY = 'DRIVER_PRODUCTIVITY';
const VEHICLE_QUERY_KEY = 'VEHICLE_PRODUCTIVITY';
const PRODUCTIVITY_CHART_QUERY_KEY = 'PRODUCTIVITY_CHART';

const useProductivity = () => {
  const useVehicleProductivitySummary = (request: ProductivitySummaryRequest) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<ProductivitySummary>({
      queryKey: [`${VEHICLE_QUERY_KEY}_SUMMARY`, JSON.stringify(request)],
      queryFn: () => getProductivityRequester().getVehicleProductivitySummary(request),
      staleTime: 300000,
    });
  };

  const useDriverProductivitySummary = (request: ProductivitySummaryRequest) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<ProductivitySummary>({
      queryKey: [`${DRIVER_QUERY_KEY}_SUMMARY`, JSON.stringify(request)],
      queryFn: () => getProductivityRequester().getDriverProductivitySummary(request),
      staleTime: 300000,
    });
  };

  const useTimeSeriesChart = (request: ProductivityTimeSeriesChartRequest, enabled = true) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TimeseriesChart[]>({
      queryKey: [PRODUCTIVITY_CHART_QUERY_KEY, JSON.stringify(request)],
      queryFn: () => getProductivityRequester().getProductivityTimeSeriesChart(request),
      retry: false,
      enabled: enabled,
    });
  };

  return {
    useVehicleProductivitySummary,
    useDriverProductivitySummary,
    useTimeSeriesChart,
  };
};
export default useProductivity;
